import { AlertTriangle } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { DISABLED_LEGIBLE } from '@/Domain/components/Integration/WhatsAppPhoneNumbers/connectPanelStyles';
import { Alert, AlertDescription, AlertTitle } from '@/Domain/components/Shared/Alert/Alert';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Spinner } from '@/Domain/components/Shared/Spinner';

export type ConnectRecoveryReason =
  | 'popup_deadline_without_result'
  | 'popup_blocked'
  | 'status_not_confirmed';

// Remediation steps, not a survey: the merchant usually needs a different one than they first think.
const CAUSES: readonly { id: string; key: string }[] = [
  { id: 'admin', key: 'whatsapp_phone_numbers.setup.connect_recovery_cause_admin' },
  {
    id: 'number_in_use',
    key: 'whatsapp_phone_numbers.setup.connect_recovery_cause_number_in_use',
  },
  {
    id: 'other_provider',
    key: 'whatsapp_phone_numbers.setup.connect_recovery_cause_other_provider',
  },
  { id: 'blocker', key: 'whatsapp_phone_numbers.setup.connect_recovery_cause_blocker' },
] as const;

const BOLD_LEAD = <strong className="font-semibold" />;

interface ConnectRecoveryPanelProps {
  reason: ConnectRecoveryReason;
  onRetry: () => void;
  onContinueInTab: () => void;
  onDismiss: () => void;
  retryDisabled: boolean;
  continueDisabled: boolean;
  continueLoading?: boolean;
  docsHref: string;
}

export const ConnectRecoveryPanel = ({
  reason,
  onRetry,
  onContinueInTab,
  onDismiss,
  retryDisabled,
  continueDisabled,
  continueLoading = false,
  docsHref,
}: ConnectRecoveryPanelProps) => {
  const { t } = useTranslation(['integrations']);
  const blocked = reason === 'popup_blocked';

  return (
    <Alert variant="warning" data-testid="whatsapp-connect-recovery-panel">
      <AlertTriangle className="size-4" />
      <AlertTitle>
        {t(
          blocked
            ? 'whatsapp_phone_numbers.setup.connect_blocked_title'
            : 'whatsapp_phone_numbers.setup.connect_recovery_title',
        )}
      </AlertTitle>
      <AlertDescription className="flex flex-col gap-2">
        <p className="text-xs">
          {t(
            blocked
              ? 'whatsapp_phone_numbers.setup.connect_blocked_lead'
              : 'whatsapp_phone_numbers.setup.connect_recovery_lead',
          )}
        </p>
        {!blocked && (
          <ul className="flex flex-col gap-2 pt-0.5">
            {CAUSES.map(({ id, key }) => (
              <li
                key={id}
                // Two to three lines each, so they need spacing to read as four separate causes.
                className="flex items-start gap-2.5 text-xs leading-relaxed"
                data-testid={`whatsapp-connect-recovery-cause-${id}`}
              >
                <span aria-hidden className="mt-1.5 size-1 shrink-0 rounded-full bg-current" />
                <span>
                  <Trans t={t} i18nKey={key} components={[BOLD_LEAD]} />
                </span>
              </li>
            ))}
          </ul>
        )}
      </AlertDescription>
      <div className="flex flex-col gap-3 pt-3.5 !pl-0">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            onClick={onRetry}
            size="sm"
            variant="dark"
            className={DISABLED_LEGIBLE}
            disabled={retryDisabled}
            data-testid="whatsapp-connect-recovery-retry"
          >
            {t('whatsapp_phone_numbers.setup.connect_recovery_retry_button')}
          </Button>
          <Button
            onClick={onContinueInTab}
            size="sm"
            variant="secondary"
            disabled={continueDisabled || continueLoading}
            aria-busy={continueLoading || undefined}
            data-testid="whatsapp-connect-recovery-continue-in-tab"
          >
            {continueLoading && <Spinner className="size-4" />}
            {t('whatsapp_phone_numbers.setup.connect_recovery_in_tab_button')}
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            asChild
            size="sm"
            variant="link"
            className="text-warning-foreground h-auto px-0 py-0 text-xs font-normal"
            data-testid="whatsapp-connect-recovery-docs"
          >
            <a href={docsHref} target="_blank" rel="noopener noreferrer">
              {t('whatsapp_phone_numbers.setup.connect_recovery_docs_link')}
            </a>
          </Button>
          <Button
            asChild
            size="sm"
            variant="link"
            className="text-warning-foreground h-auto px-0 py-0 text-xs font-normal"
            data-testid="whatsapp-connect-recovery-support"
          >
            <a href="mailto:support@quickchat.ai">
              {t('whatsapp_phone_numbers.setup.connect_recovery_support_link')}
            </a>
          </Button>
          {/* Never disabled: the only guaranteed way back to the normal sheet. */}
          <Button
            onClick={onDismiss}
            size="sm"
            variant="link"
            className="text-warning-foreground h-auto px-0 py-0 text-xs font-normal"
            data-testid="whatsapp-connect-recovery-dismiss"
          >
            {t('whatsapp_phone_numbers.setup.connect_recovery_dismiss_button')}
          </Button>
        </div>
      </div>
    </Alert>
  );
};
